import React, {useEffect, useState} from 'react';
import PropTypes from "prop-types";
import {serviceRetailerDropdown} from "../../utils/retailerDisplayFormatter";
import {useServiceBase} from "../Components/ReactTable/useServiceBase";
import {INDEX_FIELD, RETAILER_ID_FIELD, RETAILER_ID_NAME_COLUMN} from "../Components/ReactTable/common_custom_columns";
import Page from "../Components/Page";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import EditableReactTableV2 from "../Components/ReactTable/EditableReactTableV2";
import {Breadcrumbs} from "../Components/Breadcrumbs";
import {EditFormBooleanDropdownInputField, EditFormDropdownInputField} from "../Components/ReactTable/EditRecordForm";
import {makeApolloClientCall} from "../../Api/makeApolloApiCall";
import moment from "moment";
import 'moment-timezone'
import {getUserEmail} from "../../auth/accessTokenValidator";
import CollapsableContent from "../Components/CollapsableContent";
import {makeApiCall} from "../../Api/makeApiCall";
import _ from "lodash";
import {getPriorityRetailerHistory} from "./GetPriorityRetailerHistory";


export function sortDataBydataQCWeekId(data) {
    if (data)
        data.sort((a, b) => {
            return a.qcWeek - b.qcWeek;
        })
    return data;
}

const PriorityRetailers = ({displayHelmetHeader = true}) => {
    const endpointUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/priority_retailers/`
    const {loading, data, onRowEditConfirmed, onRowDeleteConfirmed} = useServiceBase({endpointUrl});
    const weekAgoDate = moment(Date.now()).subtract(7, 'days').format('YYYY-MM-DD');

    const MAX_QC_HISTORY_ARRAY_LENGTH = 20;

    const [periodOptions, updatePeriodOptions] = useState([]);
    const [defaultWeekId, setDefaultWeekId] = useState();
    const [isLoading, updateIsLoading] = useState(true);
    const [retailers, setRetailers] = useState([]);
    const [receiptLag, setReceiptLag] = useState([]);
    const SLD_PRIORITY_RETAILER_COLUMNS = [
        RETAILER_ID_NAME_COLUMN,
        {
            header: "Rollup Day", filterable: true,
            cell: ({row}) => {
                const rollupday = _.find(retailers.data, {retailerId: row.original.retailerId})?.rollUpDayOfWeek
                return <div>{rollupday}</div>
            }
        },
        {
            header: "Receipt Lag", filterable: true,
            cell: ({row}) => {
                const days = _.find(receiptLag.data, {retailerId: row.original.retailerId})?.receiptLag
                return <div>{days}</div>
            }
        },
        {header: "QC Approved", accessorKey: "qcApproved"},
        {header: "QC Fail Reason(s)", accessorKey: "qcFailReason",
            cell: ({row}) => {
                const retailerId = row.original.retailerId;
                if(row.original.qcFailReason){
                    return (<div>
                        {row.original.qcFailReason.includes('ingestion_failed') ? <a href={`/ingestedLogs?includeSuccess=false&includeShortCircuit=false&includeBulkOnly=false&includeCurrentlyRunning=false&includeFailures=true&hideOnboarding=true&startDate=${weekAgoDate}&retailer=${retailerId}&fileType=sales`} target="_blank" rel="noopener noreferrer">Ingestion Failed</a> : <div />}
                        <br/>
                        {row.original.qcFailReason.includes('known_item_percentage') ? <a href={`/ingestedLogs?includeSuccess=true&includeShortCircuit=false&includeBulkOnly=false&includeCurrentlyRunning=false&includeFailures=true&hideOnboarding=true&startDate=${weekAgoDate}&retailer=${retailerId}&fileType=sales`} target="_blank" rel="noopener noreferrer">Known Item Percentage</a> : <div />}
                        <br/>
                        {row.original.qcFailReason.includes('alert') ? <a href={`/alerts/log?days=7&retailerId=${retailerId}`} target="_blank" rel="noopener noreferrer">Unacknowledged Alerts</a> : <div />}
                    </div>)
                }else{
                    return <></>
                }

            }, size: 200},
        {header: "QC Week id", accessorKey: "qcWeek"},
        {header: "QC User", accessorKey: "qcUser"},
        {header: "Date of QC", accessorKey: "dateOfQc"},
        {
            header: "QC History", cell: ({row}) => {
                const expandedContent = getPriorityRetailerHistory(row.original.history);
                return <CollapsableContent expandedContent={expandedContent} collapsedContent={"show history"}/>;
            }, size: 400
        }
    ]

    useEffect(() => {
        updateIsLoading(true)
        Promise.all([
            makeApolloClientCall(` { periods { weekId endDate isCurrentPeriodWeek } } `),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/retailers/`),
            makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/receiptLag/`)
        ])
            .then(([
                       periods, retailerRollup, receiptLagDays
                   ]) => {
                updatePeriodOptions(periods.periods.map((week) => ({value: week.weekId, label: week.endDate})));
                setDefaultWeekId(periods.periods.find(item => item.isCurrentPeriodWeek === true).weekId)
                setReceiptLag(receiptLagDays)
                setRetailers(retailerRollup)
            }).finally(() => {
            updateIsLoading(false)
        });


    }, []);

    function cleanUpAndSubmitData(currentData) {
        if (currentData["qcApproved"] !== undefined) {
            currentData["qcUser"] = getUserEmail()
            const inp = Date.now();
            currentData["dateOfQc"] = moment(inp).tz("America/Chicago").format('MM/DD/YYYY HH:mm:ss')
            const update = {
                "user": currentData["qcUser"],
                "date": currentData["dateOfQc"],
                "qcApproved": currentData["qcApproved"],
                "week": currentData["qcWeek"]
            };
            if (currentData["history"] === undefined) {
                currentData["history"] = [update]
            } else {
                currentData["history"].unshift(update)

                if (currentData["history"].length > MAX_QC_HISTORY_ARRAY_LENGTH) {
                    currentData["history"].sort((a, b) => b.date - a.date)
                    while (currentData["history"].length > MAX_QC_HISTORY_ARRAY_LENGTH) {
                        currentData["history"].pop()
                    }
                }
            }
            if(currentData["qcApproved"] || ["", undefined].includes(currentData["qcFailReason"])) {
                currentData["qcFailReason"] = []
            }
        }
        onRowEditConfirmed(currentData)
    }



    return (<Page name={"Priority Retailers: Store Insights"} displayHelmetHeader={displayHelmetHeader}
                  breadCrumbs={<Breadcrumbs
                      items={[{label: "Configurations"}, {label: "Retailer"}, {label: "Priority: Store Insights"}]}/>}>
        <LoadingWrapper loading={loading || isLoading}>
            <EditableReactTableV2
                filterable
                data={sortDataBydataQCWeekId(data.data)}
                initialState={{pagination: {pageSize: 25}, sorting: [{id: "qcApproved", desc: false}, {id: "dateOfQc", desc: true}]}}
                columns={SLD_PRIORITY_RETAILER_COLUMNS}
                editTable={[INDEX_FIELD,
                    RETAILER_ID_FIELD(serviceRetailerDropdown().data),
                    {id: "qcApproved", defaultValue: false, Component: args => <EditFormBooleanDropdownInputField {...args} />},
                    {id: "qcWeek", defaultValue: defaultWeekId, Component: args => <EditFormDropdownInputField  {...args} options={periodOptions}/>},
                ]}
                onRowEditConfirmed={cleanUpAndSubmitData}
                onRowDeleteConfirmed={onRowDeleteConfirmed}
            />
        </LoadingWrapper>
    </Page>);
}

PriorityRetailers.propTypes = {
    displayHelmetHeader: PropTypes.bool,
}

export default PriorityRetailers;
